import Color from 'color'
import styled from 'styled-components'

import { Calendar } from '@/ui'

interface StyledCalendarProps {
  loading?: boolean
  weekString?: string
}

export const StyledCalendar = styled(Calendar)<StyledCalendarProps>`
  &&& {
    border: 0;

    .react-calendar__viewContainer {
      position: relative;
      border-bottom: 1px solid #e9eff0;
      border-right: 1px solid #e9eff0;

      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: white;
        z-index: 10;
        visibility: ${props => (props.loading ? 'visible' : 'hidden')};
        opacity: 0.5;
      }
    }

    &&& {
      .react-calendar__month-view__weekNumbers {
        min-width: 55px;
        flex-basis: unset !important;
        align-self: stretch;
        margin-top: 36px;

        &::before {
          display: flex;
          content: '${props => props.weekString}';
          height: 36px;
          min-width: 55px;
          position: absolute;
          top: 0;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: #a1a5b0;
          border-top: 1px solid #e9eff0;
          border-left: 1px solid #e9eff0;
        }
      }
    }

    .react-calendar__month-view__weekdays {
      height: 36px;

      .react-calendar__month-view__weekdays__weekday {
        border-top: 1px solid #e9eff0;
        border-left: 1px solid #e9eff0;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      abbr {
        font-size: 14px;
        font-weight: 500;
        color: #08153a;
        text-decoration: none;
        text-transform: capitalize;
      }
    }

    .react-calendar__tile {
      overflow: visible !important;
      position: relative;
      height: 162px;
      font-size: 1.1em;

      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        border-left: 1px solid #e9eff0;
        border-top: 1px solid #e9eff0;
      }

      abbr,
      .day-of-week {
        height: 38px;
        width: 38px;
        border-radius: 19px;
        padding: 8px 5px;
        position: absolute;
        top: 6px;
        left: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-transform: capitalize;
      }

      &.react-calendar__tile--now {
        background: none;

        abbr {
          background: ${props => props.theme.primaryColor};
          color: ${props => props.theme.secondaryFontColor} !important;
        }
      }

      &:focus,
      &.react-calendar__tile--active {
        background: transparent;
        abbr {
          color: ${props => props.theme.primaryFontColor};
        }
      }
    }

    .react-calendar__month-view__days__day--weekend {
      background: repeating-linear-gradient(
        -60deg,
        #fff,
        #fff 8px,
        #f3f3f5 8px,
        #f3f3f5 9px
      );
    }
  }
`

export const CalendarEntry = styled.div<{ length?: number }>`
  position: absolute;
  display: flex;
  align-items: center;
  left: 0;
  right: 0;
  height: 22px;
  z-index: 1;
  width: ${props => (props.length || 1) * 100}%;
  text-align: left;
  padding-left: 8px;
  background-color: ${props => Color(props.color).lightness(99).string()};
  border-top: 1px solid ${props => props.color};
  border-bottom: 1px solid ${props => props.color};
  cursor: pointer;

  &.unapproved {
    border-top-style: dashed;
    border-bottom-style: dashed;
  }

  &:hover {
    background-color: ${props => Color(props.color).lightness(98).string()};
  }

  &&&& {
    &.vacation-period-start {
      left: 5px;
      border-left: 1px solid ${props => props.color};
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
      width: ${(props: any) => `calc(${(props.length || 1) * 100}% - 4px)`};

      &.unapproved {
        border-left-style: dashed;
      }
    }

    &.vacation-period-end {
      right: 4px;
      border-right: 1px solid ${props => props.color};
      border-top-right-radius: 3px;
      border-bottom-right-radius: 3px;
      width: ${(props: any) => `calc(${(props.length || 1) * 100}% - 4px)`};

      &.unapproved {
        border-right-style: dashed;
      }
    }

    &.vacation-period-start-end {
      left: 5px;
      right: 4px;
      border: 1px solid ${props => props.color};
      border-radius: 3px;
      width: ${(props: any) => `calc(${(props.length || 1) * 100}% - 9px)`};

      &.unapproved {
        border-style: dashed;
      }
    }

    &.pointy-start {
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
      border-left: 1px solid ${props => props.color};
      left: 5px;
      width: ${(props: any) => `calc(${(props.length || 1) * 100}% - 5px)`};

      &.vacation-period-end {
        width: ${(props: any) => `calc(${(props.length || 1) * 100}% - 9px)`};
      }

      &.unapproved {
        border-left-style: dashed;
      }
    }

    &.pointy-end {
      border-top-right-radius: 12px;
      border-bottom-right-radius: 12px;
      border-right: 1px solid ${props => props.color};

      &.unapproved {
        border-right-style: dashed;
      }
    }

    &.not-all-day {
      border: 0;
      background: none;
      padding-left: 3px;

      &:hover {
        background: ${props => props.color}1a;
      }

      &::before {
        content: '';
        display: flex;
        width: 8px;
        height: 8px;
        border-radius: 3px;
        margin-right: 4px;
        background: ${props => props.color};
      }

      &.unapproved {
        &::before {
          border: 1px dashed ${props => props.color};
          background: none;
        }
      }
    }
  }

  .vacation-name {
    font-size: 0.8em;
    color: ${props => props.color};
    height: 20px;
    line-height: 22px;
    white-space: nowrap;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    pointer-events: none;
    position: relative;
    z-index: 1;
  }
`

export const MoreWrapper = styled.div`
  width: 200px;
  margin-right: -10px;
  margin: -5px -10px 0 0;

  > div {
    max-height: 350px;
    overflow-y: auto;
    padding-right: 10px;
  }

  &&&&& {
    ${CalendarEntry} {
      position: relative;
      vertical-align: middle;
      margin-top: 5px;

      left: 0;
      right: 0;
    }
  }
`

export const MoreButton = styled.div`
  color: ${props => props.theme.primaryFontColor};
  position: absolute;
  bottom: 4px;
  left: 10px;
  font-size: 0.8em;
  height: 24px;
  line-height: 24px;
  vertical-align: middle;
  font-weight: bold;
`
